import FJCreationEditUndoRedoManager from '../../components/FJMainPreview/FJCreationEditPanel/js/FJCreationEditUndoRedoManager';
import { BACKGROUND_TYPE } from '../../components/FJMainPreview/FJCreationEditPanel/js/FJPhotoRemoveBgCanvas';
import {
    FJViewCanvasSingleton,
    MOUSE_EVENT_TYPE,
} from '../../components/FJMainPreview/FJCreationEditPanel/js/FJViewCanvas';
import FJAppConstant from '../../constants/FJAppConstant';
import FJConstant from '../../constants/FJConstant';
import FJAiMaskStore from '../../store/FJAiMaskStore';
import FJLocalStore from '../../store/FJLocalStore';
import FJMessageStore from '../../store/FJMessageStore';
import FJUtil from '../../util/FJUtil';
import {
    setSliceAiMaskActive,
    setSliceBrushSize,
    setSliceCompareLineScale,
    setSliceCurBrushType,
    setSliceDisableCompare,
    setSliceDisableFitButton,
    setSliceDisableReset,
    setSliceEditImage,
    setSliceEditImageRect,
    setSliceEditImageSize,
    setSliceHasBrushRedo,
    setSliceHasBrushUndo,
    setSliceIsMouseMove,
    setSliceIsPhotoRemoveBg,
    setSliceIsShowBrushInCenter,
    setSliceIsSlideCompare,
    setSliceOriginImage,
    setSliceOriginImageSize,
    setSliceZoom,
} from '../slices/FJSliceBrushEditCommon';
import FJActionCommon from './FJActionCommon';
import FJActionImageManipulation from './FJActionImageManipulation';
import FJActionImageStroke from './FJActionImageStroke';
import FJActionModal from './FJActionModal';
import FJActionObjectErase from './FJActionObjectErase';
import FJActionPhotoBgRemover from './FJActionPhotoBgRemover';

const EDIT_MASK_MAX_SIZE = 1000; // 10M

let aiMaskLoading = false;
let _undoRedoData = null;

const FJActionBrushEditCommon = {
    /**
     * @desc 更新画笔操作是否可以撤销和重做
     * @returns
     */
    updateBrushUndoRedoAction: function () {
        return async (dispatch, getState) => {
            let hasUndo = getState().brushEditCommon.hasBrushUndo;
            let hasRedo = getState().brushEditCommon.hasBrushRedo;

            switch (getState().common.appType) {
                case FJAppConstant.PHOTO_REMOVE_BG: {
                    const res = dispatch(FJActionPhotoBgRemover.getUndoRedoAction());
                    if (res) {
                        hasUndo = res.hasUndo;
                        hasRedo = res.hasRedo;
                    }

                    break;
                }
                default:
                    hasUndo = FJCreationEditUndoRedoManager.hasUndo();
                    hasRedo = FJCreationEditUndoRedoManager.hasRedo();

                    break;
            }

            dispatch(setSliceHasBrushUndo(hasUndo));
            dispatch(setSliceHasBrushRedo(hasRedo));
        };
    },

    /**
     * @desc 更新对比功能是否禁用
     * @returns
     */
    updateDisableCompareAction: function () {
        return dispatch => {
            const isPhotoRemoveBg = FJViewCanvasSingleton.isPhotoRemoveBg;
            if (isPhotoRemoveBg) {
                dispatch(setSliceDisableCompare(false));
                return;
            }

            const originImageLoaded = FJViewCanvasSingleton.getOriginImageLoaded();
            const editImageUrl = FJViewCanvasSingleton.getEditImageUrl();
            const originImageUrl = FJViewCanvasSingleton.getOriginImageUrl();

            dispatch(setSliceDisableCompare(!originImageLoaded || editImageUrl === originImageUrl));
        };
    },

    /**
     * @desc 更新是否是去背景模式
     * @returns
     */
    updateIsPhotoRemoveBgAction: function () {
        return async dispatch => {
            const isPhotoRemoveBg = FJViewCanvasSingleton.isPhotoRemoveBg;
            dispatch(setSliceIsPhotoRemoveBg(isPhotoRemoveBg));
        };
    },

    /**
     * @desc 更新缩放
     * @returns
     */
    updateZoomAction: function () {
        return dispatch => {
            const zoom = FJViewCanvasSingleton.getZoom();
            dispatch(setSliceZoom(zoom));
            dispatch(this.updateDisableFitButtonAction());
        };
    },

    /**
     * @desc 更新编辑图片的rect
     * @returns
     */
    updateEditImageRectAction: function () {
        return async dispatch => {
            const editImageRect = FJViewCanvasSingleton.originRect;
            dispatch(setSliceEditImageRect({ ...editImageRect }));
        };
    },

    /**
     * @desc 更新滑动对比功能相关数据
     * @returns
     */
    updateSlideCompareDataAction: function () {
        return dispatch => {
            const originImageSize = FJViewCanvasSingleton.getOriginImageSize();
            const editImageSize = FJViewCanvasSingleton.getEditImageSize();

            originImageSize && dispatch(setSliceOriginImageSize(originImageSize));
            editImageSize && dispatch(setSliceEditImageSize(editImageSize));
            dispatch(this.updateCompareLineScaleAction());
            dispatch(this.updateEditImageRectAction());
        };
    },

    /**
     * @desc 更新滑动对比功能分割线位置
     * @param {number} value
     * @returns
     */
    updateCompareLineScaleAction: function (value) {
        return async dispatch => {
            if (value !== undefined) {
                FJViewCanvasSingleton.compareLineScale = value;
            }
            const compareLineScale = FJViewCanvasSingleton.compareLineScale;
            dispatch(setSliceCompareLineScale(compareLineScale));
            FJViewCanvasSingleton.draw();
        };
    },

    /**
     * @desc 更新适配按钮是否禁用
     * @returns
     */
    updateDisableFitButtonAction: function () {
        return dispatch => {
            const isCanFit = FJViewCanvasSingleton.isCanFit();
            dispatch(setSliceDisableFitButton(!isCanFit));
        };
    },

    /**
     * @desc 更新画笔大小
     * @returns
     */
    updateBrushSizeAction: function (size, type = 'input') {
        return async (dispatch, getState) => {
            const { editImage } = getState().brushEditCommon;

            if (type === 'mousedown' && editImage) {
                dispatch(setSliceIsShowBrushInCenter(true));
            }

            if (type === 'mouseup' && editImage) {
                dispatch(setSliceIsShowBrushInCenter(false));
            }

            dispatch(setSliceBrushSize(size));
        };
    },

    /**
     * @desc 设置编辑图片
     * @param {{width: number, height: number, src: string, name: string, id: number}} imageData
     * @returns
     */
    setEditImageAction: function (imageData) {
        return async (dispatch, getState) => {
            if (!imageData) {
                dispatch(setSliceEditImage(null));
                return;
            }

            const { editImage } = getState().brushEditCommon;
            if (!editImage || editImage.src !== imageData.src) {
                // 图片更改后切换到创作面板
                FJViewCanvasSingleton.clear();
                dispatch(FJActionCommon.setHeaderTabIndexAction(0));
                dispatch(setSliceEditImage({ ...imageData }));

                // 图片更改后初始化
                FJViewCanvasSingleton.setZoom(1, null, { zoomFit: false });
                dispatch(this.updateZoomAction());
                dispatch(setSliceIsMouseMove(false));
                dispatch(setSliceDisableFitButton(true));

                FJCreationEditUndoRedoManager.clear();
                dispatch(this.updateBrushUndoRedoAction());
                dispatch(setSliceDisableReset(true));
            }
        };
    },

    /**
     * @desc 设置原图
     * @param {{width: number, height: number, src: string, name: string, id: number}} imageData
     * @returns
     */
    setOriginImageAction: function (imageData) {
        return async dispatch => {
            if (!imageData) {
                dispatch(setSliceOriginImage(null));
                return;
            }
            dispatch(setSliceOriginImage(imageData));
            await FJViewCanvasSingleton.initOriginImage(imageData.src);
            dispatch(this.updateDisableCompareAction());
        };
    },

    /**
     * @desc 清空 ai mask
     * @returns
     */
    clearAiMaskAction: function () {
        return async () => {
            FJViewCanvasSingleton.clearAiMask();
        };
    },

    /**
     * @desc 重置mask画布
     * @returns
     */
    resetMaskAction: function () {
        return async (dispatch, getState) => {
            const { isPhotoRemoveBg, curBrushType } = getState().brushEditCommon;

            if (
                isPhotoRemoveBg &&
                getState().common.appType === FJAppConstant.PHOTO_REMOVE_BG &&
                curBrushType === MOUSE_EVENT_TYPE.NONE
            ) {
                // undo 和 redo 处理时机不一致 将画布重置方法传入 内部处理
                await dispatch(FJActionPhotoBgRemover.resetMaskAction());
            } else {
                FJViewCanvasSingleton.reset();
            }

            getState().common.appType !== FJAppConstant.PHOTO_REMOVE_BG && dispatch(setSliceDisableReset(true));
            dispatch(this.updateBrushUndoRedoAction());
            dispatch(FJActionPhotoBgRemover.updateDataByFJViewCanvas());
        };
    },

    /**
     * @desc 设置当前画笔类型
     * @param {MOUSE_EVENT_TYPE} curBrushType
     * @returns
     */
    setCurBrushTypeAction: function (curBrushType) {
        return async (dispatch, getState) => {
            const { isMouseMove, isSlideCompare, isPhotoRemoveBg } = getState().brushEditCommon;
            const oldCurBrushType = getState().brushEditCommon.curBrushType;

            if (isMouseMove && curBrushType !== MOUSE_EVENT_TYPE.NONE) {
                dispatch(setSliceIsMouseMove(false));
            }

            if (isSlideCompare && curBrushType !== MOUSE_EVENT_TYPE.NONE) {
                dispatch(this.enterOrEndSlideCompareAction(false));
            }

            dispatch(setSliceCurBrushType(curBrushType));
            if (
                isPhotoRemoveBg &&
                curBrushType !== MOUSE_EVENT_TYPE.NONE &&
                oldCurBrushType === MOUSE_EVENT_TYPE.NONE
            ) {
                _undoRedoData = FJCreationEditUndoRedoManager.getUndoAndRedoData();
                FJCreationEditUndoRedoManager.clear();
                dispatch(setSliceDisableReset(true));
                FJViewCanvasSingleton.enterBrushInPhotoRemoveBg();
                dispatch(this.updateBrushUndoRedoAction());
            }
        };
    },

    /**
     * @desc 适配画布
     * @returns
     */
    fitZoomAndPositionAction: function () {
        return dispatch => {
            FJViewCanvasSingleton.setZoom(1, null, { isFitClick: true });
            dispatch(this.updateZoomAction());
            dispatch(this.updateDisableFitButtonAction());
        };
    },

    /**
     * @desc 进入或者结束滑动对比模式
     * @returns
     */
    enterOrEndSlideCompareAction: function (bool, isUpdate = true) {
        return async dispatch => {
            bool && dispatch(this.updateSlideCompareDataAction());
            bool ? FJViewCanvasSingleton.enterSlideCompare(isUpdate) : FJViewCanvasSingleton.endSlideCompare(isUpdate);
            dispatch(setSliceIsSlideCompare(bool));
        };
    },

    /**
     * @desc 设置ai mask是否启用
     * @param {boolean} value
     * @returns
     */
    setAiMaskActive: function (value) {
        return async (dispatch, getState) => {
            dispatch(setSliceAiMaskActive(value));
            if (value) {
                const __successCallback = () => {};

                const __errorCallback = () => {};
                dispatch(this.initAiMaskCanvas(__successCallback, __errorCallback));
            } else {
                // 关闭后 取消选中 QUICK_SELECTION
                const curBrushType = getState().brushEditCommon.curBrushType;

                if (curBrushType === MOUSE_EVENT_TYPE.QUICK_SELECTION) {
                    dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.MASK_DRAW));
                }
            }
        };
    },

    /**
     * @desc 初始化ai mask画布
     * @returns
     */
    initAiMaskCanvas: function (successCallback, errorCallback) {
        return async (dispatch, getState) => {
            const { curMenuIndex } = getState().photoBgRemover;
            const appType = getState().common.appType;
            if (
                aiMaskLoading ||
                (appType !== FJAppConstant.OBJECT_ERASER &&
                    (appType !== FJAppConstant.PHOTO_REMOVE_BG || curMenuIndex === 0))
            ) {
                return;
            }

            const { aiMaskActive } = getState().brushEditCommon;
            if (!aiMaskActive) return;

            aiMaskLoading = true;
            dispatch(FJActionModal.showGenerateLoadingModalAction(FJLocalStore._('recognizing...')));

            // 延迟微任务执行 防止界面更新失帧
            setTimeout(async () => {
                try {
                    let editImageBlob = await FJViewCanvasSingleton.getEditImageBlob(EDIT_MASK_MAX_SIZE);
                    if (!editImageBlob) {
                        throw new Error('editImageBlob is null');
                    }

                    // 检查登录
                    let userSpaceType = 0;
                    await new Promise(resolve => {
                        FJUtil.checkUserSpace({
                            loginCallback: () => {
                                dispatch(setSliceAiMaskActive(true));
                                resolve();
                            },
                            errorCallback: () => {
                                resolve();
                            },
                            beforeLoginCallback: () => {
                                aiMaskLoading = false;
                                dispatch(setSliceAiMaskActive(false));
                                dispatch(FJActionModal.hideGenerateLoadingModalAction());
                                // 移动端需要切换绘制模式
                                dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.MASK_DRAW));
                            },
                        }).then(data => {
                            userSpaceType = data;
                            if (userSpaceType !== 2) {
                                resolve();
                            }
                        });
                    });

                    if (userSpaceType > 0) {
                        dispatch(FJActionCommon.userSpaceChangeAction(userSpaceType));
                        throw new Error('isLogin is false');
                    }

                    aiMaskLoading = true;
                    dispatch(FJActionModal.showGenerateLoadingModalAction(FJLocalStore._('recognizing...')));

                    const { maskData, id } = await FJAiMaskStore.getMaskData(editImageBlob);
                    if (maskData) {
                        await FJViewCanvasSingleton.initAiMaskCanvas(maskData, id);
                        dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.QUICK_SELECTION));

                        successCallback && successCallback();
                    }
                } catch (error) {
                    FJMessageStore.addMessage(FJLocalStore._('failed-to-recognize-please-try-again'), 'error');
                    dispatch(this.setAiMaskActive(false));
                    dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.MASK_DRAW));
                    errorCallback && errorCallback();
                } finally {
                    aiMaskLoading = false;
                    dispatch(FJActionModal.hideGenerateLoadingModalAction());
                }
            }, 200);
        };
    },

    /**
     * @desc 进入去背景模式
     * @returns
     */
    enterPhotoRemoveBgAction: function () {
        return async dispatch => {
            await FJViewCanvasSingleton.enterPhotoRemoveBg();
            dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.NONE));
            FJCreationEditUndoRedoManager.clear();
            dispatch(this.updateBrushUndoRedoAction());
            dispatch(this.updateDisableCompareAction());
            dispatch(this.updateIsPhotoRemoveBgAction());
        };
    },

    /**
     * @desc 结束去背景模式
     * @returns
     */
    endPhotoRemoveBgAction: function (isDraw = true) {
        return dispatch => {
            FJViewCanvasSingleton.endPhotoRemoveBg(isDraw);
            dispatch(this.updateIsPhotoRemoveBgAction());
            dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.NONE));
        };
    },

    /**
     * @desc 去背景模式画笔修改应用
     * @returns
     */
    applyPhotoRemoveBgBrushAction: function () {
        return dispatch => {
            FJCreationEditUndoRedoManager.clear();
            _undoRedoData && FJCreationEditUndoRedoManager.setUndoAndRedoData(_undoRedoData);

            FJViewCanvasSingleton.applyPhotoRemoveBgBrush();
            dispatch(this.updateBrushUndoRedoAction());
            dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.NONE));
            FJViewCanvasSingleton.mouseEventType = MOUSE_EVENT_TYPE.NONE;

            dispatch(FJActionPhotoBgRemover.applyPhotoRemoveBgBrushAction());
        };
    },

    /**
     * @desc 去背景模式画笔修改取消
     * @returns
     */
    cancelPhotoRemoveBgBrushAction: function () {
        return async (dispatch, getState) => {
            FJCreationEditUndoRedoManager.clear();
            _undoRedoData && FJCreationEditUndoRedoManager.setUndoAndRedoData(_undoRedoData);

            dispatch(this.setCurBrushTypeAction(MOUSE_EVENT_TYPE.NONE));
            FJViewCanvasSingleton.mouseEventType = MOUSE_EVENT_TYPE.NONE;
            FJViewCanvasSingleton.cancelPhotoRemoveBgBrush();
            dispatch(this.updateBrushUndoRedoAction());

            let hasUndo = getState().brushEditCommon.hasBrushUndo;
            dispatch(setSliceDisableReset(!hasUndo));
            dispatch(FJActionPhotoBgRemover.cancelPhotoRemoveBgBrushAction());
        };
    },

    /**
     * @desc 设置描边信息
     * @param {Object} data
     * @param {Object} data.strokeInfo
     * @param {string} changeType
     * @returns
     */
    setStrokeInfoAction: function (data, changeType = FJConstant.CHANGE_END, isStrokeTypeChange = false) {
        return async dispatch => {
            const { strokeInfo } = data;
            if (changeType === FJConstant.CHANGE_START || isStrokeTypeChange) {
                FJViewCanvasSingleton.addUndo();
            }

            FJViewCanvasSingleton.setStrokeInfo(strokeInfo);
            dispatch(FJActionImageStroke.onStrokeChangeAction(strokeInfo));

            if (changeType === FJConstant.CHANGE_END || isStrokeTypeChange) {
                dispatch(this.updateBrushUndoRedoAction());
            }
        };
    },

    /**
     * @desc 设置背景图片
     * @param {string} url
     * @returns
     */
    setBackgroundImageAction: function (backgroundInfo) {
        return async dispatch => {
            if (!backgroundInfo) return;
            try {
                FJViewCanvasSingleton.addUndo();
                const result = await FJViewCanvasSingleton.setBackgroundInfo({
                    backgroundType: BACKGROUND_TYPE.IMAGE,
                    backgroundImage: backgroundInfo,
                });
                dispatch(this.updateBrushUndoRedoAction());
                return result;
            } catch (error) {
                return false;
            }
        };
    },

    /**
     * @desc 设置背景颜色
     * @param {string} color
     * @returns
     */
    setBackgroundColorAction: function (color, changeType = FJConstant.CHANGE_END) {
        return async dispatch => {
            if (changeType === FJConstant.CHANGE_START || changeType === FJConstant.CLICK) {
                FJViewCanvasSingleton.addUndo();
            }

            FJViewCanvasSingleton.setBackgroundInfo({
                backgroundType: BACKGROUND_TYPE.COLOR,
                backgroundColor: color,
            });

            if (changeType === FJConstant.CHANGE_END) {
                dispatch(this.updateBrushUndoRedoAction());
            }
        };
    },

    /**
     * @desc 重置背景
     * @returns
     */
    resetBackgroundAction: function () {
        return dispatch => {
            const backgroundInfo = FJViewCanvasSingleton.getBackgroundInfo();
            if (!backgroundInfo || backgroundInfo.type === BACKGROUND_TYPE.TRANSPARENT) return;

            FJViewCanvasSingleton.addUndo();

            FJViewCanvasSingleton.setBackgroundInfo({
                backgroundType: BACKGROUND_TYPE.TRANSPARENT,
                backgroundImage: null,
                backgroundColor: null,
            });
            dispatch(this.updateBrushUndoRedoAction());
            return true;
        };
    },

    /**
     * @desc 编辑图片加载完成后后执行
     * @returns
     */
    editImageLoadedAfterAction: function (loadSuccess) {
        return async (dispatch, getState) => {
            dispatch(FJActionBrushEditCommon.updateDisableCompareAction());

            switch (getState().common.appType) {
                case FJAppConstant.PHOTO_COLORIZE:
                case FJAppConstant.PHOTO_RESTORATION:
                    dispatch(FJActionImageManipulation.updateCreationEditDataAction());

                    break;

                case FJAppConstant.OBJECT_ERASER:
                    dispatch(FJActionObjectErase.updateEditImageAfterAction());
                    break;
                case FJAppConstant.PHOTO_REMOVE_BG: {
                    dispatch(FJActionPhotoBgRemover.updateCreationEditDataAction(loadSuccess));
                    break;
                }
            }
        };
    },

    /**
     * @desc undo
     * @returns
     */
    undoAction: function () {
        return async (dispatch, getState) => {
            switch (getState().common.appType) {
                case FJAppConstant.PHOTO_REMOVE_BG:
                    dispatch(FJActionPhotoBgRemover.undoAction());
                    break;
                default:
                    await FJViewCanvasSingleton.undo();
                    dispatch(setSliceDisableReset(false));
                    dispatch(this.updateBrushUndoRedoAction());
                    break;
            }
        };
    },

    /**
     * @desc redo
     * @returns
     */
    redoAction: function () {
        return async (dispatch, getState) => {
            switch (getState().common.appType) {
                case FJAppConstant.PHOTO_REMOVE_BG:
                    dispatch(FJActionPhotoBgRemover.redoAction());
                    break;
                default:
                    await FJViewCanvasSingleton.redo();
                    dispatch(setSliceDisableReset(false));
                    dispatch(this.updateBrushUndoRedoAction());
                    break;
            }
        };
    },

    /**
     * @desc 重试
     * @returns
     */
    retryAction: function () {
        return async (dispatch, getState) => {
            switch (getState().common.appType) {
                case FJAppConstant.PHOTO_REMOVE_BG:
                    dispatch(FJActionPhotoBgRemover.retryAction());
                    break;
                case FJAppConstant.OBJECT_ERASER:
                    dispatch(FJActionObjectErase.reGenerateAction());
                    break;
                case FJAppConstant.PHOTO_RESTORATION:
                case FJAppConstant.PHOTO_COLORIZE:
                    dispatch(FJActionImageManipulation.reGenerateAction());
                    break;
            }
        };
    },
};

export default FJActionBrushEditCommon;
